import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import usePrograms from '@/components/admin/ProgramManagement/usePrograms';
import { IProgram } from '@/components/admin/ProgramManagement/types';
import SPAPageHeader from '../../shared/SPAPageHeader';
import AuditReportPane from './AuditReportPane';
import AuditSidebar from './AuditSidebar';
import { ProgramOption } from './contentAudit.utils';
import {
  LessonAuditSummary,
  LIBRARIES,
  MaterialAuditSummary,
  lessonLabel,
} from './contentAudit.types';

type SummaryState = 'error' | 'loaded' | 'loading';

const BATCH_POLL_INTERVAL = 15000;
const ROW_POLL_INTERVAL = 4000;
const SERVER_POLL_INTERVAL = 8000;
const RUBRIC = 'fl_pr';

const EmptyPane = ({ message }: { message: string }) => (
  <>
    <SPAPageHeader title="Content Audit" />
    <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">{message}</p>
  </>
);

// AIDEV-NOTE: SPA fork of admin/ContentAuditPage — DevDocs-style two-pane shell over the same
// content-audit APIs. Selection state lives in URL search params (program/collection/lesson/material/fl).
const ContentAuditTool = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const programId = Number(searchParams.get('program')) || null;
  const collection = searchParams.get('collection') ?? 'lessons';
  const selectedLessonId = Number(searchParams.get('lesson')) || null;
  const selectedMaterialId = Number(searchParams.get('material')) || null;
  const flOnly = searchParams.get('fl') !== '0';

  const libraryTag = collection === 'lessons' ? null : collection;
  const library = LIBRARIES.find(option => option.tag === libraryTag) ?? null;

  const { isLoading: programsLoading, programs } = usePrograms({
    attributes: ['full_title_with_edition', 'id', 'kind'],
  });
  const programOptions: ProgramOption[] = (programs as Array<IProgram & { kind?: string }>)
    .filter(program => program.kind !== 'professional_development')
    .map(program => ({ label: program.full_title_with_edition, value: program.id }));
  const program = programOptions.find(option => option.value === programId) ?? null;

  const [lessons, setLessons] = useState<LessonAuditSummary[]>([]);
  const [lessonsState, setLessonsState] = useState<SummaryState>('loading');
  const [materials, setMaterials] = useState<MaterialAuditSummary[]>([]);
  const [materialsState, setMaterialsState] = useState<SummaryState>('loading');
  const [batchResult, setBatchResult] = useState<string | null>(null);
  const [batchQueuing, setBatchQueuing] = useState(false);
  const [batchPolling, setBatchPolling] = useState(false);
  // Rail-triggered runs, keyed 'lesson-<id>' / 'rm-<id>' so ids can't collide across modes. The
  // version bump remounts the detail pane when its row's run lands, so a fresh report appears.
  const [activeRunKeys, setActiveRunKeys] = useState<string[]>([]);
  const [rowRunVersions, setRowRunVersions] = useState<Record<string, number>>({});
  const rowPollTimersRef = useRef<Record<string, number>>({});
  // Server-known in-flight runs (from the run registry) survive page refreshes; refs remember the
  // previous poll so completions can remount a selected detail pane.
  const [serverAuditingLessons, setServerAuditingLessons] = useState<number[]>([]);
  const [serverAuditingMaterials, setServerAuditingMaterials] = useState<number[]>([]);
  const serverLessonRunsRef = useRef<number[]>([]);
  const serverMaterialRunsRef = useRef<number[]>([]);

  const bumpVersions = (keys: string[]) => {
    if (keys.length === 0) return;
    setRowRunVersions((versions) => {
      const next = { ...versions };
      keys.forEach((key) => { next[key] = (next[key] ?? 0) + 1; });
      return next;
    });
  };

  const updateParams = useCallback((changes: Record<string, string | null>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Refreshers double as the report pane's onReportChange, so a refresh failure must never
  // regress an already-loaded rail into the error state.
  const refreshLessons = useCallback(async () => {
    if (!programId) return;
    try {
      const { data } = await axios.get(
        Routes.plato_api_content_audit_summary_program_path(programId, { rubric: RUBRIC }),
      );
      setLessons(data.lessons ?? []);
      setLessonsState('loaded');
      const runningIds: number[] = data.auditing_ids ?? [];
      bumpVersions(serverLessonRunsRef.current
        .filter(id => !runningIds.includes(id))
        .map(id => `lesson-${id}`));
      serverLessonRunsRef.current = runningIds;
      setServerAuditingLessons(runningIds);
    } catch {
      setLessonsState(previous => (previous === 'loaded' ? previous : 'error'));
    }
  }, [programId]);

  const refreshMaterials = useCallback(async () => {
    if (!programId || !libraryTag) return;
    try {
      const { data } = await axios.get(
        Routes.plato_api_library_content_audit_summary_program_path(
          programId,
          { fl_only: flOnly, rubric: RUBRIC, tag: libraryTag },
        ),
      );
      setMaterials(data.materials ?? []);
      setMaterialsState('loaded');
      const runningIds: number[] = data.auditing_ids ?? [];
      bumpVersions(serverMaterialRunsRef.current
        .filter(id => !runningIds.includes(id))
        .map(id => `rm-${id}`));
      serverMaterialRunsRef.current = runningIds;
      setServerAuditingMaterials(runningIds);
    } catch {
      setMaterialsState(previous => (previous === 'loaded' ? previous : 'error'));
    }
  }, [flOnly, libraryTag, programId]);

  useEffect(() => {
    setLessons([]);
    setLessonsState('loading');
    setMaterials([]);
    setMaterialsState('loading');
    setBatchResult(null);
    setBatchPolling(false);
    setServerAuditingLessons([]);
    setServerAuditingMaterials([]);
    serverLessonRunsRef.current = [];
    serverMaterialRunsRef.current = [];
  }, [programId]);

  useEffect(() => {
    refreshLessons();
  }, [refreshLessons]);

  useEffect(() => {
    setMaterials([]);
    setMaterialsState('loading');
    refreshMaterials();
  }, [refreshMaterials]);

  // Auto-select the first row — including when a deep-linked id isn't in the loaded list (stale link).
  useEffect(() => {
    if (libraryTag || lessons.length === 0) return;
    if (selectedLessonId && lessons.some(lesson => lesson.lesson_id === selectedLessonId)) return;
    updateParams({ lesson: String(lessons[0].lesson_id) });
  }, [lessons, libraryTag, selectedLessonId, updateParams]);

  useEffect(() => {
    if (!libraryTag || materials.length === 0) return;
    if (selectedMaterialId && materials.some(material => material.reference_material_id === selectedMaterialId)) return;
    updateParams({ material: String(materials[0].reference_material_id) });
  }, [libraryTag, materials, selectedMaterialId, updateParams]);

  // Keep polling while the server reports in-flight runs — this is what makes a refreshed page
  // live: rows finish, counts update, and the poll stops itself once the registry drains.
  const serverAuditingIds = library ? serverAuditingMaterials : serverAuditingLessons;
  const serverAuditingActive = serverAuditingIds.length > 0;
  useEffect(() => {
    if (!serverAuditingActive) return undefined;
    const refresh = library ? refreshMaterials : refreshLessons;
    const timer = window.setInterval(() => refresh(), SERVER_POLL_INTERVAL);
    return () => window.clearInterval(timer);
  }, [library, refreshLessons, refreshMaterials, serverAuditingActive]);

  // Batch audits land over minutes (staggered waves) — keep the rail counts moving while they do.
  useEffect(() => {
    if (!batchPolling) return undefined;
    const refresh = libraryTag ? refreshMaterials : refreshLessons;
    const timer = window.setInterval(() => refresh(), BATCH_POLL_INTERVAL);
    return () => window.clearInterval(timer);
  }, [batchPolling, libraryTag, refreshLessons, refreshMaterials]);

  useEffect(() => () => {
    Object.values(rowPollTimersRef.current).forEach(timer => window.clearInterval(timer));
  }, []);

  const changeProgram = (option: ProgramOption | null) => {
    updateParams({
      collection: null,
      fl: null,
      lesson: null,
      material: null,
      program: option ? String(option.value) : null,
    });
  };

  const changeCollection = (value: string) => {
    if (value === collection) return;
    setBatchPolling(false);
    setBatchResult(null);
    updateParams(value === 'lessons'
      ? { collection: null, material: null }
      : { collection: value, material: null });
  };

  const finishRowRun = (key: string) => {
    const timer = rowPollTimersRef.current[key];
    if (timer) window.clearInterval(timer);
    delete rowPollTimersRef.current[key];
    setActiveRunKeys(keys => keys.filter(activeKey => activeKey !== key));
    setRowRunVersions(versions => ({ ...versions, [key]: (versions[key] ?? 0) + 1 }));
  };

  const pollRowRun = (key: string, jobId: string) => {
    const refresh = key.startsWith('rm-') ? refreshMaterials : refreshLessons;
    rowPollTimersRef.current[key] = window.setInterval(async () => {
      try {
        const { data } = await axios.get(Routes.plato_api_job_status_path(jobId));
        const jobStatus = data.data?.status;
        if (jobStatus === 'failed' || jobStatus === 'rate_limited') {
          finishRowRun(key);
          setBatchResult(data.data?.error?.message || 'An audit failed. Please try again.');
        } else if (jobStatus === 'completed') {
          finishRowRun(key);
          refresh();
        }
      } catch {
        finishRowRun(key);
      }
    }, ROW_POLL_INTERVAL);
  };

  // The report pane reports its own runs here so the rail row shows auditing too.
  const setPaneAuditing = (id: number, value: boolean) => {
    const key = libraryTag ? `rm-${id}` : `lesson-${id}`;
    if (value) {
      setActiveRunKeys(keys => (keys.includes(key) ? keys : [...keys, key]));
    } else {
      finishRowRun(key);
    }
  };

  const runRowAudit = async (id: number) => {
    const key = libraryTag ? `rm-${id}` : `lesson-${id}`;
    const path = libraryTag
      ? Routes.plato_api_content_audit_reference_material_path(id)
      : Routes.plato_api_content_audit_lesson_path(id);
    setActiveRunKeys(keys => [...keys, key]);
    try {
      const { data } = await axios.post(path, { rubric: RUBRIC });
      if (data.status === 'rate_limited') {
        finishRowRun(key);
        setBatchResult('Rate limited by the AI gateway. Please try again later.');
        return;
      }
      pollRowRun(key, data.job_id);
    } catch {
      finishRowRun(key);
      setBatchResult('Could not start the audit. Please try again.');
    }
  };

  const runBatch = async () => {
    if (!programId) return;
    setBatchResult(null);
    setBatchQueuing(true);
    try {
      const { data } = libraryTag
        ? await axios.post(
          Routes.plato_api_library_content_audit_batch_program_path(programId),
          { fl_only: flOnly, rubric: RUBRIC, tag: libraryTag },
        )
        : await axios.post(Routes.plato_api_content_audit_batch_program_path(programId), { rubric: RUBRIC });
      if (data.status === 'rate_limited') {
        setBatchResult('Rate limited by the AI gateway. Please try again later.');
        return;
      }
      setBatchResult(`Queued ${data.enqueued} · ${data.skipped} up to date`);
      if (data.enqueued > 0) {
        setBatchPolling(true);
        // The registry knows the queued rows immediately — refresh now instead of waiting 15s.
        (libraryTag ? refreshMaterials : refreshLessons)();
      }
    } catch {
      setBatchResult('Could not start the batch. Please try again.');
    } finally {
      setBatchQueuing(false);
    }
  };

  const railState = library ? materialsState : lessonsState;
  const selectedLesson = lessons.find(lesson => lesson.lesson_id === selectedLessonId) ?? null;
  const selectedMaterial = materials.find(
    material => material.reference_material_id === selectedMaterialId,
  ) ?? null;
  const localRunIds = activeRunKeys
    .filter(key => key.startsWith(library ? 'rm-' : 'lesson-'))
    .map(key => Number(key.split('-')[1]));
  const activeRunIds = Array.from(new Set([...localRunIds, ...serverAuditingIds]));
  const notRunCount = library
    ? materials.filter(material => !material.has_report && material.auditable !== false).length
    : lessons.filter(lesson => !lesson.has_report).length;

  const detailVersion = library
    ? rowRunVersions[`rm-${selectedMaterialId}`] ?? 0
    : rowRunVersions[`lesson-${selectedLessonId}`] ?? 0;

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <AuditSidebar
        auditingIds={activeRunIds}
        batchQueuing={batchQueuing}
        batchResult={batchResult}
        collection={collection}
        flOnly={flOnly}
        lessons={lessons}
        library={library}
        materials={materials}
        notRunCount={notRunCount}
        program={program}
        programOptions={programOptions}
        programsLoading={programsLoading}
        railState={railState}
        selectedLessonId={selectedLessonId}
        selectedMaterialId={selectedMaterialId}
        onChangeCollection={changeCollection}
        onChangeProgram={changeProgram}
        onRunBatch={runBatch}
        onRunRow={runRowAudit}
        onSelectLesson={id => updateParams({ lesson: String(id) })}
        onSelectMaterial={id => updateParams({ material: String(id) })}
        onToggleFlOnly={() => updateParams({ fl: flOnly ? '0' : null, material: null })}
      />
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        {!program && <EmptyPane message="Pick a program on the left to review its audits." />}
        {program && !library && selectedLesson && (
          <AuditReportPane
            key={`lesson-${selectedLesson.lesson_id}-v${detailVersion}`}
            auditing={activeRunIds.includes(selectedLesson.lesson_id)}
            lessonId={selectedLesson.lesson_id}
            programId={programId ?? undefined}
            rubric={RUBRIC}
            title={lessonLabel(selectedLesson)}
            onAuditingChange={value => setPaneAuditing(selectedLesson.lesson_id, value)}
            onReportChange={refreshLessons}
          />
        )}
        {program && library && selectedMaterial && (
          <AuditReportPane
            key={`rm-${selectedMaterial.reference_material_id}-v${detailVersion}`}
            auditable={selectedMaterial.auditable !== false}
            auditing={activeRunIds.includes(selectedMaterial.reference_material_id)}
            referenceMaterialId={selectedMaterial.reference_material_id}
            rubric={RUBRIC}
            title={selectedMaterial.title}
            onAuditingChange={value => setPaneAuditing(selectedMaterial.reference_material_id, value)}
            onReportChange={refreshMaterials}
          />
        )}
        {program && railState === 'loading' && !selectedLesson && !selectedMaterial && (
          <EmptyPane message="Loading collection…" />
        )}
        {program && railState !== 'loading' && !library && !selectedLesson && (
          <EmptyPane message="Select a lesson to see its review." />
        )}
        {program && railState !== 'loading' && library && !selectedMaterial && (
          <EmptyPane message="Select a material to see its review." />
        )}
      </div>
    </div>
  );
};

export default ContentAuditTool;
